/**
 * 新規管理画面用API（JWT認証）
 */

const API_BASE_URL = '/api/manage-new';
const TOKEN_KEY = 'auth_access_token';

/**
 * JWT トークンを取得
 */
const getToken = (): string | null => {
  return localStorage.getItem(TOKEN_KEY);
};

/**
 * 認証付きfetchラッパー
 */
const authFetch = async (url: string, options: RequestInit = {}): Promise<Response> => {
  const token = getToken();
  
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...(options.headers || {}),
  };

  if (token) {
    (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(url, {
    ...options,
    headers,
  });

  // 401エラーの場合はログアウト処理
  if (response.status === 401) {
    localStorage.removeItem(TOKEN_KEY);
    window.location.href = '/meeting/manage-new/login';
    throw new Error('認証エラー: 再度ログインしてください');
  }

  return response;
};

/**
 * APIレスポンスの型定義
 */
export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
}

/**
 * AIマネージャーの型定義
 */
export interface AiManager {
  id: number;
  company_id: number;
  name: string;
  persona_summary: string | null;
  analysis_prompt: string | null;
  sort_order: number | null;
  execution_interval_seconds: number | null;
  llm_model_id: number | null;
  is_active: boolean;
  api_model_name_display: string | null;
  api_model_code: string | null;
}

/**
 * チェックテンプレートアイテムの型定義
 */
export interface CheckTemplateItem {
  id: number;
  template_id: number;
  item_text: string;
  sort_order: number | null;
  is_active: boolean;
}

/**
 * チェックテンプレートの型定義
 */
export interface CheckTemplate {
  id: number;
  company_id: number;
  template_name: string;
  sort_order: number | null;
  is_active: boolean;
  item_count: number;
  items: CheckTemplateItem[];
}

/**
 * LLMモデルの型定義
 */
export interface LlmModel {
  id: number;
  api_model_code: string;
  api_model_name_display: string;
  model_description: string | null;
  cost_description: string | null;
  sort_order: number;
}

/**
 * 議事録の型定義（一覧用）
 */
export interface MeetingMinutesListItem {
  id: number;
  recording_id: string;
  minutes_generated_at: string | null;
  minutes_meeting_name: string | null;
  minutes_meeting_goal: string | null;
  server_file_path: string | null;
}

/**
 * 議事録の型定義（詳細用）
 */
export interface MeetingMinutesDetail {
  id: number;
  recording_id: string;
  minutes_generated_at: string | null;
  minutes_meeting_name: string | null;
  minutes_meeting_goal: string | null;
  minutes_markdown: string | null;
  server_file_path: string | null;
  started_at: string | null;
  ended_at: string | null;
}

// ========== AIマネージャー ==========

/**
 * AIマネージャー一覧を取得
 */
export const getAiManagers = async (): Promise<AiManager[]> => {
  const response = await authFetch(`${API_BASE_URL}/ai-managers`);
  const data = await response.json();
  
  if (!response.ok) {
    throw new Error(data.message || 'AIマネージャーの取得に失敗しました');
  }
  
  return data.data;
};

/**
 * AIマネージャーを取得
 */
export const getAiManager = async (id: number): Promise<AiManager> => {
  const response = await authFetch(`${API_BASE_URL}/ai-managers/${id}`);
  const data = await response.json();
  
  if (!response.ok) {
    throw new Error(data.message || 'AIマネージャーの取得に失敗しました');
  }
  
  return data.data;
};

/**
 * AIマネージャーを作成
 */
export const createAiManager = async (
  payload: {
    name: string;
    persona_summary?: string;
    analysis_prompt?: string;
    sort_order?: number;
    execution_interval_seconds?: number;
    llm_model_id?: number;
  }
): Promise<{ id: number }> => {
  const response = await authFetch(`${API_BASE_URL}/ai-managers`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  const data = await response.json();
  
  if (!response.ok) {
    throw new Error(data.message || 'AIマネージャーの作成に失敗しました');
  }
  
  return data;
};

/**
 * AIマネージャーを更新
 */
export const updateAiManager = async (
  id: number,
  payload: {
    name?: string;
    persona_summary?: string;
    analysis_prompt?: string;
    sort_order?: number;
    execution_interval_seconds?: number;
    llm_model_id?: number | null;
  }
): Promise<void> => {
  const response = await authFetch(`${API_BASE_URL}/ai-managers/${id}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
  const data = await response.json();
  
  if (!response.ok) {
    throw new Error(data.message || 'AIマネージャーの更新に失敗しました');
  }
};

/**
 * AIマネージャーを削除
 */
export const deleteAiManager = async (id: number): Promise<void> => {
  const response = await authFetch(`${API_BASE_URL}/ai-managers/${id}`, {
    method: 'DELETE',
  });
  const data = await response.json();
  
  if (!response.ok) {
    throw new Error(data.message || 'AIマネージャーの削除に失敗しました');
  }
};

/**
 * AIマネージャーを一括削除
 */
export const deleteAiManagersBatch = async (ids: number[]): Promise<void> => {
  const response = await authFetch(`${API_BASE_URL}/ai-managers/batch-delete`, {
    method: 'POST',
    body: JSON.stringify({ ids }),
  });
  const data = await response.json();
  
  if (!response.ok) {
    throw new Error(data.message || 'AIマネージャーの一括削除に失敗しました');
  }
};

// ========== チェックテンプレート ==========

/**
 * チェックテンプレート一覧を取得
 */
export const getCheckTemplates = async (): Promise<CheckTemplate[]> => {
  const response = await authFetch(`${API_BASE_URL}/check-templates`);
  const data = await response.json();
  
  if (!response.ok) {
    throw new Error(data.message || 'チェックテンプレートの取得に失敗しました');
  }
  
  return data.data;
};

/**
 * チェックテンプレートを取得
 */
export const getCheckTemplate = async (id: number): Promise<CheckTemplate> => {
  const response = await authFetch(`${API_BASE_URL}/check-templates/${id}`);
  const data = await response.json();
  
  if (!response.ok) {
    throw new Error(data.message || 'チェックテンプレートの取得に失敗しました');
  }
  
  return data.data;
};

/**
 * チェックテンプレートを作成
 */
export const createCheckTemplate = async (
  payload: {
    template_name: string;
    sort_order?: number;
    items?: Array<{ item_text: string; sort_order?: number }>;
  }
): Promise<{ id: number }> => {
  const response = await authFetch(`${API_BASE_URL}/check-templates`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  const data = await response.json();
  
  if (!response.ok) {
    throw new Error(data.message || 'チェックテンプレートの作成に失敗しました');
  }
  
  return data;
};

/**
 * チェックテンプレートを更新
 */
export const updateCheckTemplate = async (
  id: number,
  payload: {
    template_name?: string;
    sort_order?: number;
    items?: Array<{ id?: number; item_text: string; sort_order?: number }>;
  }
): Promise<void> => {
  const response = await authFetch(`${API_BASE_URL}/check-templates/${id}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
  const data = await response.json();
  
  if (!response.ok) {
    throw new Error(data.message || 'チェックテンプレートの更新に失敗しました');
  }
};

/**
 * チェックテンプレートを削除
 */
export const deleteCheckTemplate = async (id: number): Promise<void> => {
  const response = await authFetch(`${API_BASE_URL}/check-templates/${id}`, {
    method: 'DELETE',
  });
  const data = await response.json();
  
  if (!response.ok) {
    throw new Error(data.message || 'チェックテンプレートの削除に失敗しました');
  }
};

/**
 * チェックテンプレートを一括削除
 */
export const deleteCheckTemplatesBatch = async (ids: number[]): Promise<void> => {
  const response = await authFetch(`${API_BASE_URL}/check-templates/batch-delete`, {
    method: 'POST',
    body: JSON.stringify({ ids }),
  });
  const data = await response.json();
  
  if (!response.ok) {
    throw new Error(data.message || 'チェックテンプレートの一括削除に失敗しました');
  }
};

// ========== LLMモデル ==========

/**
 * LLMモデル一覧を取得
 */
export const getLlmModels = async (): Promise<LlmModel[]> => {
  const response = await authFetch(`${API_BASE_URL}/llm-models`);
  const data = await response.json();
  
  if (!response.ok) {
    throw new Error(data.message || 'LLMモデルの取得に失敗しました');
  }
  
  return data.data;
};

// ========== 議事録 ==========

/**
 * 議事録一覧を取得
 */
export const getMinutesList = async (): Promise<MeetingMinutesListItem[]> => {
  const response = await authFetch(`${API_BASE_URL}/minutes`);
  const data = await response.json();
  
  if (!response.ok) {
    throw new Error(data.message || '議事録の取得に失敗しました');
  }
  
  return data.data;
};

/**
 * 議事録詳細を取得
 */
export const getMinutesDetail = async (id: number): Promise<MeetingMinutesDetail> => {
  const response = await authFetch(`${API_BASE_URL}/minutes/${id}`);
  const data = await response.json();
  
  if (!response.ok) {
    throw new Error(data.message || '議事録の取得に失敗しました');
  }
  
  return data.data;
};

/**
 * 議事録の音声ファイルURLを取得
 */
export const getMinutesAudioUrl = (id: number): string => {
  const token = getToken();
  return `${API_BASE_URL}/minutes/${id}/audio?token=${encodeURIComponent(token || '')}`;
};

/**
 * 議事録を削除（単体）
 */
export const deleteMinutes = async (id: number): Promise<void> => {
  const response = await authFetch(`${API_BASE_URL}/minutes/${id}`, {
    method: 'DELETE',
  });
  const data = await response.json();
  
  if (!response.ok) {
    throw new Error(data.message || '議事録の削除に失敗しました');
  }
};

/**
 * 議事録を一括削除
 */
export const deleteMinutesBatch = async (ids: number[]): Promise<void> => {
  const response = await authFetch(`${API_BASE_URL}/minutes/batch-delete`, {
    method: 'POST',
    body: JSON.stringify({ ids }),
  });
  const data = await response.json();
  
  if (!response.ok) {
    throw new Error(data.message || '議事録の一括削除に失敗しました');
  }
};

// ========== 話者分離（Diarization） ==========

/**
 * 話者分離セグメントの型定義
 */
export interface DiarizedSegment {
  id: number;
  speaker_id: number;
  speaker_name: string | null;
  text: string;
  start_time: number;
  end_time: number;
  confidence: number | null;
}

/**
 * 話者情報の型定義
 */
export interface Speaker {
  speaker_id: number;
  speaker_name: string | null;
}

/**
 * 話者分離レスポンスの型定義
 */
export interface DiarizeResponse {
  success: boolean;
  is_new_transcription?: boolean;
  exists?: boolean;
  speakers: Speaker[];
  segments: DiarizedSegment[];
}

/**
 * 話者分離付き文字起こしを実行
 * - 既存データがあればDBから取得
 * - なければDeepgram APIを呼び出してDBに保存
 */
export const transcribeDiarize = async (id: number): Promise<DiarizeResponse> => {
  const response = await authFetch(`${API_BASE_URL}/minutes/${id}/transcribe-diarize`, {
    method: 'POST',
  });
  const data = await response.json();
  
  if (!response.ok) {
    throw new Error(data.message || '話者分離に失敗しました');
  }
  
  return data;
};

/**
 * 話者分離データを取得（DBのみ）
 */
export const getDiarizedTranscription = async (id: number): Promise<DiarizeResponse> => {
  const response = await authFetch(`${API_BASE_URL}/minutes/${id}/diarize`);
  const data = await response.json();
  
  if (!response.ok) {
    throw new Error(data.message || '話者分離データの取得に失敗しました');
  }
  
  return data;
};

/**
 * 話者名を更新
 */
export const updateSpeakerName = async (
  minutesId: number,
  speakerId: number,
  speakerName: string,
): Promise<{ success: boolean; updated_count: number }> => {
  const response = await authFetch(`${API_BASE_URL}/minutes/${minutesId}/speakers/${speakerId}`, {
    method: 'PUT',
    body: JSON.stringify({ speaker_name: speakerName }),
  });
  const data = await response.json();
  
  if (!response.ok) {
    throw new Error(data.message || '話者名の更新に失敗しました');
  }
  
  return data;
};

// ========== 文字起こしセグメント ==========

/**
 * 文字起こしセグメントの型定義
 */
export interface TranscriptionSegment {
  id: number;
  segment_sequence: number;
  text: string;
  received_at: string;
  has_audio: boolean;
}

/**
 * 文字起こしセグメント一覧を取得
 */
export const getTranscriptionSegments = async (id: number): Promise<TranscriptionSegment[]> => {
  const response = await authFetch(`${API_BASE_URL}/minutes/${id}/segments`);
  const data = await response.json();
  
  if (!response.ok) {
    throw new Error(data.message || 'セグメントの取得に失敗しました');
  }
  
  return data.data;
};

/**
 * セグメント音声URLを取得
 */
export const getSegmentAudioUrl = (minutesId: number, segmentId: number): string => {
  const token = getToken();
  return `${API_BASE_URL}/minutes/${minutesId}/segments/${segmentId}/audio?token=${encodeURIComponent(token || '')}`;
};

// ========== ユーザ管理 (admin_users) ==========

/**
 * ユーザの型定義
 */
export interface AdminUser {
  admin_id: number;
  company_id: number;
  admin_code: string | null;
  admin_number: string | null;
  admin_name: string | null;
  admin_email: string;
  admin_email_sub: string | null;
  status: string;
  is_admin: number;
  created_at: string | null;
  updated_at: string | null;
}

/**
 * ユーザ一覧を取得
 */
export const getAdminUsers = async (): Promise<AdminUser[]> => {
  const response = await authFetch(`${API_BASE_URL}/admin-users`);
  const data = await response.json();
  
  if (!response.ok) {
    throw new Error(data.message || 'ユーザの取得に失敗しました');
  }
  
  return data.data;
};

/**
 * ユーザを取得
 */
export const getAdminUser = async (adminId: number): Promise<AdminUser> => {
  const response = await authFetch(`${API_BASE_URL}/admin-users/${adminId}`);
  const data = await response.json();
  
  if (!response.ok) {
    throw new Error(data.message || 'ユーザの取得に失敗しました');
  }
  
  return data.data;
};

/**
 * ユーザを作成
 */
export const createAdminUser = async (
  payload: {
    admin_number?: string;
    admin_name: string;
    admin_email: string;
    admin_email_sub?: string;
    is_admin: number;
    password: string;
  }
): Promise<{ admin_id: number }> => {
  const response = await authFetch(`${API_BASE_URL}/admin-users`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  const data = await response.json();
  
  if (!response.ok) {
    throw new Error(data.message || 'ユーザの作成に失敗しました');
  }
  
  return data;
};

/**
 * ユーザを更新
 */
export const updateAdminUser = async (
  adminId: number,
  payload: {
    admin_number?: string;
    admin_name?: string;
    admin_email?: string;
    admin_email_sub?: string;
    is_admin?: number;
    password?: string;
  }
): Promise<void> => {
  const response = await authFetch(`${API_BASE_URL}/admin-users/${adminId}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });
  const data = await response.json();
  
  if (!response.ok) {
    throw new Error(data.message || 'ユーザの更新に失敗しました');
  }
};

/**
 * ユーザを削除
 */
export const deleteAdminUser = async (adminId: number): Promise<void> => {
  const response = await authFetch(`${API_BASE_URL}/admin-users/${adminId}`, {
    method: 'DELETE',
  });
  const data = await response.json();
  
  if (!response.ok) {
    throw new Error(data.message || 'ユーザの削除に失敗しました');
  }
};

/**
 * ユーザを一括削除
 */
export const deleteAdminUsersBatch = async (ids: number[]): Promise<void> => {
  const response = await authFetch(`${API_BASE_URL}/admin-users/batch-delete`, {
    method: 'POST',
    body: JSON.stringify({ ids }),
  });
  const data = await response.json();
  
  if (!response.ok) {
    throw new Error(data.message || 'ユーザの一括削除に失敗しました');
  }
};

